import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useI18n } from '../i18n/i18n';
import { getExerciseDetails } from '../services/apiClient';
import { getExerciseById } from '../services/customExerciseService';
import { getLocalizedExerciseNameById } from '../utils/exerciseName';
import SeoHead from '../components/SeoHead';
import Breadcrumbs from '../components/Breadcrumbs';
import './ExerciseDetails.css';

const muscleImageMap = {
    trapezius: 'Trapezius',
    deltoids: 'Deltoids',
    rear_deltoids: 'Rear_Deltoids',
    chest: 'Chest',
    upper_back: 'Upper_Back',
    back: 'Back',
    lower_back: 'Lower_Back',
    biceps: 'Biceps',
    triceps: 'Triceps',
    forearms: 'Forearms',
    abs: 'Abs',
    obliques: 'Obliques',
    hip_flexors: 'Hip_Flexor',
    glutes: 'Glutes',
    legs: 'Legs',
    quadriceps: 'Quadriceps',
    hamstrings: 'Hamstrings',
    calves: 'Calves'
};

function ExerciseDetails() {
    const { exerciseId } = useParams();
    const { getToken } = useAuth();
    const { t, language } = useI18n();
    
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('guide');

    const exerciseCatalogInfo = useMemo(() => {
        return getExerciseById(exerciseId);
    }, [exerciseId]);

    useEffect(() => {
        let mounted = true;

        async function fetchDetails() {
            setLoading(true);
            setError(null);
            try {
                const data = await getExerciseDetails(getToken, exerciseId, language);
                if (mounted) {
                    setDetails(data);
                }
            } catch (err) {
                if (mounted) {
                    setError(err.message);
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        }

        fetchDetails();

        return () => {
            mounted = false;
        };
    }, [exerciseId, language, getToken]);

    // Determine the main muscle group image
    const normalizedId = (exerciseId || "").toLowerCase();
    const primaryMuscle = (normalizedId.includes('deadlift') || normalizedId.includes('hyperextension') ? 'lower_back' : null)
                        || exerciseCatalogInfo?.muscleGroups?.find(mg => mg.priority === 'primary')?.group 
                        || exerciseCatalogInfo?.muscleGroups?.[0]?.group
                        || (muscleImageMap[normalizedId] ? normalizedId : null)
                        || (normalizedId.includes('bench') || normalizedId.includes('chest') || normalizedId.includes('fly') || normalizedId.includes('push_up') ? 'chest' : null)
                        || (normalizedId.includes('squat') || normalizedId.includes('lunge') || normalizedId.includes('leg_press') ? 'quadriceps' : null)
                        || (normalizedId.includes('row') || normalizedId.includes('pull') || normalizedId.includes('lat') ? 'back' : null)
                        || (normalizedId.includes('shrug') ? 'trapezius' : null)
                        || (normalizedId.includes('curl') ? 'biceps' : null)
                        || (normalizedId.includes('tricep') || normalizedId.includes('dip') ? 'triceps' : null)
                        || (normalizedId.includes('press') || normalizedId.includes('overhead') ? 'deltoids' : null)
                        || (normalizedId.includes('crunch') || normalizedId.includes('plank') || normalizedId.includes('situp') ? 'abs' : null)
                        || (normalizedId.includes('calf') || normalizedId.includes('calves') ? 'calves' : null);
    
    const muscleImageName = primaryMuscle ? muscleImageMap[primaryMuscle] : null;

    const displayTitle = details?.name 
        || exerciseCatalogInfo?.name 
        || getLocalizedExerciseNameById(exerciseId, t, "")
        || (exerciseId ? exerciseId.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "");

    const effectiveDetails = details || (muscleImageName || exerciseCatalogInfo ? {
        name: displayTitle,
        equipment: exerciseCatalogInfo?.equipment || null,
        instructions: exerciseCatalogInfo?.instructions || [],
        common_mistakes: exerciseCatalogInfo?.commonMistakes || []
    } : null);

    // If no guide instructions are returned, default the view to the target muscles tab
    useEffect(() => {
        if (!details?.instructions?.length && muscleImageName) {
            setActiveTab('muscles');
        }
    }, [details, muscleImageName]);

    if (loading) {
        return (
            <div className="exercise-details-page">
                <Breadcrumbs />
                <div className="loading-state">Loading...</div>
            </div>
        );
    }

    if (!effectiveDetails) {
        return (
            <div className="exercise-details-page">
                <Breadcrumbs />
                <SeoHead
                    title={t("exerciseDetails.seoTitle", { exercise: displayTitle })}
                    description={t("exerciseDetails.seoDescription", { exercise: displayTitle })}
                />
                <div className="error-state">
                    <p>{t("exerciseDetails.noData")}</p>
                    <div className="back-button-container">
                        <Link to={`/LogExercise/${exerciseId}`} className="back-button">
                            {t("exerciseDetails.back")}
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="exercise-details-page">
            <SeoHead
                title={t("exerciseDetails.seoTitle", { exercise: displayTitle })}
                description={t("exerciseDetails.seoDescription", { exercise: displayTitle })}
            />
            
            <Breadcrumbs />
            
            <div className="exercise-details-header">
                <h1>{displayTitle}</h1>
                {effectiveDetails.equipment && (
                    <div className="exercise-equipment">
                        <strong>{t("exerciseDetails.equipment")}</strong> {effectiveDetails.equipment}
                    </div>
                )}
            </div>

            <div className="tabs-container">
                <button 
                    className={`tab-button ${activeTab === 'guide' ? 'active' : ''}`}
                    onClick={() => setActiveTab('guide')}
                >
                    {t("exerciseDetails.tabGuide")}
                </button>
                <button 
                    className={`tab-button ${activeTab === 'muscles' ? 'active' : ''}`}
                    onClick={() => setActiveTab('muscles')}
                >
                    {t("exerciseDetails.tabMuscles")}
                </button>
            </div>

            <div className="tab-content">
                {activeTab === 'guide' && (
                    <div className="guide-content">
                        {effectiveDetails.diagram_url && (
                            <img 
                                src={effectiveDetails.diagram_url} 
                                alt={`${displayTitle} diagram`} 
                                className="api-diagram" 
                            />
                        )}

                        {effectiveDetails.instructions && effectiveDetails.instructions.length > 0 && (
                            <>
                                <h2>{t("exerciseDetails.instructions")}</h2>
                                <ol className="instructions-list">
                                    {effectiveDetails.instructions.map((step, idx) => (
                                        <li key={idx}>{step}</li>
                                    ))}
                                </ol>
                            </>
                        )}

                        {effectiveDetails.common_mistakes && effectiveDetails.common_mistakes.length > 0 && (
                            <>
                                <h2>{t("exerciseDetails.commonMistakes")}</h2>
                                <ul className="mistakes-list">
                                    {effectiveDetails.common_mistakes.map((mistake, idx) => (
                                        <li key={idx}>{mistake}</li>
                                    ))}
                                </ul>
                            </>
                        )}

                        {(!effectiveDetails.instructions || effectiveDetails.instructions.length === 0) && !effectiveDetails.diagram_url && (
                            <p>{t("exerciseDetails.noData")}</p>
                        )}
                    </div>
                )}

                {activeTab === 'muscles' && (
                    <div className="muscles-content">
                        {muscleImageName ? (
                            <img 
                                src={`/images/muscles/${muscleImageName}.webp`} 
                                alt={`Target muscles: ${primaryMuscle}`} 
                                className="anatomy-image"
                            />
                        ) : (
                            <p>No anatomy image available for this exercise.</p>
                        )}
                    </div>
                )}
            </div>

            <div className="back-button-container">
                <Link to={`/LogExercise/${exerciseId}`} className="back-button">
                    {t("exerciseDetails.back")}
                </Link>
            </div>
        </div>
    );
}

export default ExerciseDetails;
